import http from './httpconfig'
import store from '@/store'
import { getCookie } from '../cookie'
import { cancelHTTPRequests, addCustomErrors } from '@/services/utils'
import { isAuthTokenPresent } from '../utils'
import { cookieConstant } from '../constants'
import { checkErrorFromSocial } from '../utils'
import API_ERRORS from '../constants/api-error'
import AppWarn from '../errors/app-warns'
import { maskSensitiveDataWithEncryption } from '../crypto/index'
import { cloneDeep } from 'lodash'

async function receiveMsgHistory(historyType, payload = {}) {
  try {
    const result  = await http.request({
      method: 'post',
      url: '/api/webhook-history',
      data : payload,
      headers: {
        ...generateHeaders(),
        historyType
      },
      cancelToken: cancelHTTPRequests()
    })
    return result
  }
  catch (error) {
    const customSocialError = checkErrorFromSocial(error?.response)
    customSocialError.customMessage = API_ERRORS.WEBHOOK_HISTORY
    throw customSocialError
  }
}

async function receiveMsgHistoryV2(historyType, payload = {}) {
  try {
    const result  = await http.request({
      method: 'post',
      url: '/api/webhook-history-v2',
      data : payload,
      headers: {
        ...generateHeaders(),
        historyType
      },
      cancelToken: cancelHTTPRequests()
    })
    return result
  }
  catch (error) {
    const customSocialError = checkErrorFromSocial(error?.response)
    customSocialError.customMessage = API_ERRORS.WEBHOOK_HISTORY
    throw customSocialError
  }
}

async function receiveMsgHistoryV3(historyType, payload = {}, additionalHeaders = {}) {
  try {
    const result  = await http.request({
      method: 'post',
      url: '/api/v3/webhook-history',
      data : payload,
      headers: {
        ...additionalHeaders,
        ...generateHeaders(),
        historyType
      },
      cancelToken: cancelHTTPRequests()
    })
    return result
  }
  catch (error) {
    const customSocialError = checkErrorFromSocial(error?.response)
    customSocialError.customMessage = API_ERRORS.WEBHOOK_HISTORY
    throw customSocialError
  }
}

async function sendAgentMessage(payload = {}) {
  try {
    const result  = await http.request({
      method: 'post',
      url: '/api/webhook-agent-message',
      data : payload,
      headers: generateHeaders(),
      cancelToken: cancelHTTPRequests()
    })
    checkErrorFromSocial(result)
    return result
  }
  catch (err) {
    const customSocialError = checkErrorFromSocial(err?.response)
    customSocialError.customMessage = API_ERRORS.AGENT_WEBHOOK_MESSAGE
    throw new AppWarn({
      name: 'Action',
      message:`Error while sending agent message: ${err.message}`,
      error: addCustomErrors(customSocialError) || {}
    })
  }
}

async function sendAgentInfo(payload = {}) {
  try {
    const result  = await http.request({
      method: 'post',
      url: '/api/webhook-agent-info',
      data : payload,
      headers: generateHeaders(),
      cancelToken: cancelHTTPRequests()
    })
    return result
  }
  catch (error) {
    const customSocialError = checkErrorFromSocial(error?.response)
    customSocialError.customMessage = API_ERRORS.AGENT_INFO_WEBHOOK_MESSAGE
    throw customSocialError
  }
}

async function sendWebhookEvent(payload = {}) {
  const conversationId = payload?.requestBody?.conversationId
  try {
    const result = await http.request({
      method: 'post',
      url: '/api/webhook-event',
      data: payload,
      headers: { ...generateHeaders(), ...(conversationId) && {'x-conversation-id': conversationId} },
      cancelToken: cancelHTTPRequests()
    })
    return result;
  } catch (error) {
    const customSocialError = checkErrorFromSocial(error?.response)
    customSocialError.customMessage = API_ERRORS.WEBHOOK_EVENT
    throw customSocialError
  }
}

async function sendUserMessage(payload = {}) {
  try {
    const botPCIPolicy = store.getters?.botPCIPolicy || {}
    const { text, label } = payload.requestBody.messagePayload
    const shouldMask = store.state.widgetConfig?.isSensitiveDataMaskingEnabled && !text.startsWith('event://;')
    const webhookMessagePaload = cloneDeep(payload)
    if(shouldMask) {
      webhookMessagePaload.requestBody.messagePayload.text = await maskSensitiveDataWithEncryption(botPCIPolicy, text, store.getters.formEncryptionPublicKey)
      webhookMessagePaload.requestBody.messagePayload.label = await maskSensitiveDataWithEncryption(botPCIPolicy, label, store.getters.formEncryptionPublicKey)
    }
    const result = await http.request({
      method: 'post',
      url: '/api/webhook-message',
      data: webhookMessagePaload,
      headers: generateHeaders(),
      cancelToken: cancelHTTPRequests()
    })
    return result;
  } catch (error) {
    const customSocialError = checkErrorFromSocial(error?.response)
    customSocialError.customMessage = API_ERRORS.WEBHOOK_MESSAGE
    throw customSocialError
  }
}

const generateHeaders = (additionalHeaders = []) => {
  const conversationId = getCookie(cookieConstant.SESSION.CONVERSATION_ID)
  const headers = {
    'Content-Type': 'application/json',
    'x-bot-ref-id': store?.getters?.getBotRefId,
    ...(conversationId) && {'x-conversation-id': conversationId},
    'x-widget-id': store?.getters?.getWidgetId,
    'x-user-id': store?.getters?.getUserId,
    'x-sdk': 'web'
  }
  const isAuthenticationEnabled = store.state.widgetConfig?.isAuthenticationEnabled || false
  addCustomHeader('x-auth-enabled', isAuthenticationEnabled, headers)

  if(isAuthenticationEnabled && isAuthTokenPresent()) {
    addCustomHeader('x-auth-token', store?.state?.authToken || getCookie(cookieConstant.AUTH.AUTH_TOKEN), headers)
  }
  
  additionalHeaders.forEach(header => addCustomHeader(header.key, header.value, headers))
  return headers
}

const addCustomHeader = ( key, value, headers) => {
  headers[key] = value
}

async function sendUserMessageForPromptTest(payload = {}) {
  try {
    const result = await http.request({
      method: 'post',
      url: '/api/query-analyzer',
      data: payload,
      headers: generateHeaders(),
      cancelToken: cancelHTTPRequests()
    })
    return result;
  } catch (error) {
    const customSocialError = checkErrorFromSocial(error?.response)
    customSocialError.customMessage = API_ERRORS.WEBHOOK_MESSAGE
    throw customSocialError
  }
}

async function responseAnalyzerSendUserMessage(payload = {}) {
  try {
    const result = await http.request({
      method: 'post',
      url: '/api/response-analyzer',
      data: payload,
      headers: generateHeaders(),
      cancelToken: cancelHTTPRequests()
    })
    return result;
  } catch (error) {
    const customSocialError = checkErrorFromSocial(error?.response)
    customSocialError.customMessage = API_ERRORS.WEBHOOK_MESSAGE
    throw customSocialError
  }
}

export {
  receiveMsgHistory,
  sendUserMessage,
  generateHeaders,
  sendAgentMessage,
  sendAgentInfo,
  sendWebhookEvent,
  receiveMsgHistoryV2,
  sendUserMessageForPromptTest,
  responseAnalyzerSendUserMessage,
  receiveMsgHistoryV3
}
